import jsonapiClient from "./jsonapi_client";
import PathMapper from "./PathMapper";

const entityFormHelper = {
  createJsonApiEntityData: function (entityType, bundle) {
    return {
      type: entityType + "--" + bundle,
      attributes: {},
      relationships: {},
    };
  },
  fillJsonApiDataFromFormData: function (data, bundleFieldData, formData) {
    Object.values(bundleFieldData).forEach((fieldData) => {
      const fieldType = fieldData.field_type;
      const fillMethod = this.getFillMethod(fieldType);
      if (typeof formData[fieldData.field_name] !== "undefined") {
        this.fillJsonApiDataField[fillMethod](
          data,
          fieldData,
          formData[fieldData.field_name],
        );
      }
    });
  },
  fillJsonApiDataField: {
    default: function (data, fieldData, formData) {
      if (Array.isArray(formData)) {
        data.attributes[fieldData.field_name] = formData;
      } else {
        data.attributes[fieldData.field_name] = [{ value: formData }];
      }
    },
    language: function (data, fieldData) {
      data.attributes[fieldData.field_name] = [{ value: "en" }];
    },
    link: function (data, fieldData, formData) {
      data.attributes[fieldData.field_name] = [{ uri: formData }];
    },
    entityReference: function (data, fieldData, formData) {
      if (formData) {
        // Handle entity reference fields set to use an options widget.
        if (typeof formData === "string") {
          const uuid = formData;
          const targetType = fieldData?.settings?.target_type;
          const targetBundles =
            fieldData?.settings?.handler_settings?.target_bundles;
          const allowedValues =
            fieldData?.field_storage?.settings?.allowed_values;
          if (targetType && (allowedValues || targetBundles)) {
            let bundle = null;
            if (allowedValues) {
              Object.entries(allowedValues).forEach(([bundleName, options]) => {
                if (options[uuid]) {
                  bundle = bundleName;
                }
              });
            }
            if (!bundle && targetBundles) {
              // This is the best we can do.
              bundle = Object.values(targetBundles)[0];
            }
            if (!bundle) {
              // This is valid for entity types with no bundles.
              bundle = targetType;
            }
            formData = {
              id: uuid,
              type: targetType + "--" + bundle,
            };
          }
        }
        data.relationships[fieldData.field_name] = { data: formData };
      }
    },
    textLong: function (data, fieldData, formData) {
      data.attributes[fieldData.field_name] = {
        format: "basic_html",
        value: formData,
      };
    },
    explainField: function (data, fieldData, formData) {
      data.attributes[fieldData.field_name] = formData;
    },
    time: function (data, fieldData, formData) {
      const parts = formData.split(":");
      const seconds = +parts[0] * 60 * 60 + +parts[1] * 60;
      data.attributes[fieldData.field_name] = [{ value: seconds }];
    },
  },
  getFillMethod: function (fieldType) {
    switch (fieldType) {
      case "link":
        return "link";

      case "entity_reference":
      case "og_standard_reference":
      case "message_recipient":
      case "file":
      case "image":
        return "entityReference";

      case "text_long":
        return "textLong";

      case "language":
        return "language";

      case "explainfield_list":
        return "explainField";

      case "time":
        return "time";

      default:
        return "default";
    }
  },
  getDefaultValue: function (fieldData) {
    const fieldType = fieldData.field_type;
    let defaultValue = "";
    if (
      typeof fieldData.default_value !== "undefined" &&
      fieldData.default_value.length > 0
    ) {
      defaultValue = fieldData.default_value[0].value;
    }
    switch (fieldType) {
      case "boolean":
        return !!defaultValue;

      default:
        return defaultValue;
    }
  },
  getDefaultValueFromEntity: function (fieldName, fieldType, entity) {
    switch (fieldType) {
      case "boolean":
      case "string":
      case "string_long":
      case "list_string":
      case "string_textfield":
      case "decimal":
      case "time":
        return entity.attributes[fieldName];
      case "list_integer":
        return entity.attributes[fieldName];

      case "link":
        if (entity.attributes[fieldName]) {
          return entity.attributes[fieldName].uri;
        }
        return "";

      case "entity_reference":
      case "og_standard_reference":
        // @todo roche_work Handle multiple values.
        if (
          entity.relationships[fieldName] &&
          entity.relationships[fieldName].hasOwnProperty("data")
        ) {
          if (
            Array.isArray(entity.relationships[fieldName].data) &&
            entity.relationships[fieldName].data.length
          ) {
            const values = [];
            entity.relationships[fieldName].data.forEach((reference) => {
              values.push(reference.id);
            });
            return values;
          }
          try {
            return entity.relationships[fieldName].data.id;
          } catch (exception) {
            // No-op.
          }
        }
        return "";

      case "text_long":
        if (
          entity.attributes[fieldName] &&
          entity.attributes[fieldName].value
        ) {
          return entity.attributes[fieldName].value;
        }
        return "";

      case "explainfield_list":
        if (
          Array.isArray(entity.attributes[fieldName]) &&
          entity.attributes[fieldName].length
        ) {
          if (
            entity.attributes[fieldName][0] &&
            entity.attributes[fieldName][0].hasOwnProperty("value")
          ) {
            return entity.attributes[fieldName][0];
          }
        } else if (
          entity.attributes[fieldName] &&
          entity.attributes[fieldName].hasOwnProperty("value")
        ) {
          return entity.attributes[fieldName];
        }

        return {};

      case "datetime":
        if (entity.attributes[fieldName]) {
          return entity.attributes[fieldName];
        }
        return null;

      default:
        return "";
    }
  },
  updateFormData: function (
    fieldData,
    value,
    refs,
    entityType,
    bundle,
    callback,
    errorCallback,
    context,
  ) {
    const that = this;
    let fieldVal = null;
    switch (fieldData.field_type) {
      case "file":
        that.updateFileFormData(
          fieldData,
          refs,
          entityType,
          bundle,
          callback,
          errorCallback,
          context,
        );
        return;

      case "boolean":
        if (typeof value === "string") {
          value = value === "true";
        }
        fieldVal = !!value;
        break;

      default:
        fieldVal = value;
        break;
    }
    callback.call(context, fieldVal);
  },
  updateFileFormData(
    fieldData,
    refs,
    entityType,
    bundle,
    callback,
    errorCallback,
    context,
  ) {
    const fileList = refs[fieldData.field_name].files;
    const file = fileList[0];
    const maxSize =
      fieldData.settings.max_filesize.replace(/MB/g, "") * 1000000;
    if (file.size > maxSize) {
      errorCallback(
        "The uploaded file is too large to be submitted. The file must be under " +
          fieldData.settings.max_filesize +
          ".",
        context,
      );
      return;
    }
    const url = entityType + "/" + bundle + "/" + fieldData.field_name;
    jsonapiClient.postJSONapiFile(
      url,
      file,
      (response) => {
        callback.call(context, response.data);
      },
      errorCallback,
      context,
    );
  },
  getHtmlElement: function (fieldType, fieldData = null) {
    switch (fieldType) {
      case "list_string":
      case "list_integer":
        return "select";

      case "datetime":
        return "datetime";

      case "time":
        return "time";

      case "og_standard_reference":
        return "og_standard_reference";

      case "string_long":
        return "textarea";

      case "entity_reference":
        if (fieldData.form_display.type === "entity_reference_autocomplete") {
          return "autocomplete";
        }
        return "select";

      case "explainfield_list":
        return "explain_field";

      case "date_offset_field":
        return "date_offset_field";

      case "date_recurrence":
        return "date_recurrence";

      default:
        return "input";
    }
  },
  getInputType: function (fieldType, fieldData = null) {
    switch (fieldType) {
      case "string":
      case "link":
      case "telephone":
      case "email":
        return "text";

      case "file":
      case "image":
        return "file";

      case "list_string":
      case "list_integer":
        if (fieldData.form_display.type === "options_buttons") {
          if (
            fieldData.form_display.third_party_settings
              .bioraft_core_components &&
            fieldData.form_display.third_party_settings.bioraft_core_components
              .widget_options.buttons
          ) {
            return "radio_group_toggle";
          }
          if (
            fieldData.form_display.third_party_settings
              .bioraft_core_components &&
            fieldData.form_display.third_party_settings.bioraft_core_components
              .widget_options.toggle_buttons
          ) {
            return "inline_button_toggle";
          }
          if (fieldData.field_storage.cardinality === -1) {
            return "checkbox_group";
          }
          if (
            fieldData.form_display.third_party_settings?.bioraft_core_components
              ?.widget_options?.radios_with_descriptions
          ) {
            return "radio";
          }
          return "radio_group";
        }
        return "checkbox";

      case "boolean":
        if (fieldData?.form_display?.type === "options_buttons") {
          if (
            fieldData?.form_display?.third_party_settings
              ?.bioraft_core_components?.widget_options?.buttons
          ) {
            return "radio_group_toggle";
          }
          if (
            fieldData?.form_display?.third_party_settings
              ?.bioraft_core_components?.widget_options?.toggle_buttons
          ) {
            return "inline_button_toggle";
          }
          return "radio";
        }
        return "checkbox";

      case "og_standard_reference":
        return "select";

      case "chemical_constituent_reference":
        return fieldType;

      case "entity_reference":
        if (fieldData.form_display.type === "entity_reference_autocomplete") {
          return "autocomplete";
        }
        if (fieldData.form_display.type === "options_buttons") {
          if (fieldData.field_storage.cardinality === 1) {
            return "radio_group";
          }
          return "checkbox_group";
        }
        return "select";

      case "explainfield_list":
        return "explain_field";

      case "integer":
      case "float":
      case "decimal":
        return "number";

      case "text_long":
      case "text_with_summary":
        return "wysiwyg";

      case "date_offset_field":
        return "date_offset_field";

      case "date_recurrence":
        return "date_recurrence";

      case "entity_reference_details":
        if (fieldData.form_display.type === "options_buttons") {
          if (fieldData.field_storage.cardinality === 1) {
            return "radio_group";
          }
          return "checkbox_group";
        }
        return "select";

      case "markup":
        return "markup";

      default:
        return false;
    }
  },
  getHelpText: function (fieldData) {
    if (!fieldData) {
      return null;
    }
    let text = "";
    switch (fieldData?.field_type) {
      case "file":
      case "image":
        if (fieldData.settings.hasOwnProperty("file_extensions")) {
          text +=
            "Allowed file extensions: " +
            fieldData.settings.file_extensions +
            ".";
        }
        if (fieldData.settings.hasOwnProperty("max_filesize")) {
          text += " " + fieldData.settings.max_filesize + " file size limit.";
        }
        return text;

      default:
        return null;
    }
  },
  getOptions: function (fieldData) {
    const options = [];
    const buildOptions = (allowedValues) => {
      if (Array.isArray(allowedValues)) {
        allowedValues.forEach((item) => {
          if (typeof item === "object") {
            const newValue = {};
            if (item.hasOwnProperty("uuid")) {
              newValue.value = item.uuid;
            } else if (item.hasOwnProperty("id")) {
              newValue.value = item.id;
            } else if (item.hasOwnProperty("value")) {
              newValue.value = item.value;
            }
            newValue.text = item.label || item.text;
            if (item.hasOwnProperty("description")) {
              newValue.description = item.description;
            }
            options.push(newValue);
          } else {
            options.push(item);
          }
        });
        return;
      }
      Object.entries(allowedValues).forEach(([key, item]) => {
        let hasTextValue = false;
        let pushOption = true;
        if (typeof item === "object") {
          const newValue = {};
          if (item.hasOwnProperty("uuid")) {
            newValue.value = item.uuid;
          } else if (item.hasOwnProperty("id")) {
            newValue.value = item.id;
          } else {
            // Nested object within the allowed values.
            // Example: settings: { allowed_values: { bio_research_project: { d131d8a7-265a-4e12-9163-bb6ec28f037f:"EPP Project 1" } } }
            Object.entries(item).forEach(([key2, item2]) => {
              const newValue = {};
              newValue.value = key2;
              newValue.text = item2;
              hasTextValue = true;
              options.push(newValue);
              pushOption = false;
            });
          }
          if (!hasTextValue) {
            newValue.text = item.label;
          }
          if (item.hasOwnProperty("description")) {
            newValue.description = item.description;
          }
          if (pushOption) {
            options.push(newValue);
          }
        } else {
          options.push({ value: key, text: item });
        }
      });
    };
    switch (fieldData?.field_type) {
      case "boolean":
        options.push({ value: true, text: fieldData.settings.on_label });
        options.push({ value: false, text: fieldData.settings.off_label });
        break;
      case "entity_reference":
        if (fieldData.field_storage.settings.hasOwnProperty("allowed_values")) {
          if (Array.isArray(fieldData.field_storage.settings.allowed_values)) {
            buildOptions(fieldData.field_storage.settings.allowed_values);
          }
          // These are nested one lever deeper, keyed by bundle name.
          else {
            Object.values(
              fieldData?.field_storage?.settings?.allowed_values || {},
            ).forEach((allowedValues) => {
              if (allowedValues) {
                buildOptions(allowedValues);
              }
            });
          }
        }
        break;
      default:
        if (
          fieldData?.field_storage?.settings?.hasOwnProperty("allowed_values")
        ) {
          buildOptions(fieldData.field_storage.settings.allowed_values);
        }
    }
    return options;
  },
  getInputPattern: function (fieldData) {
    return fieldData?.field_type === "link" ? "https?://.+" : null;
  },
  getInputAccept: function (fieldData) {
    let types = [];
    switch (fieldData?.field_type) {
      case "file":
      case "image":
        if (fieldData.settings.hasOwnProperty("file_extensions")) {
          types = fieldData.settings.file_extensions
            .split(/\s+/)
            .map((t) => "." + t);
        }
        return types.join(", ");

      default:
        return null;
    }
  },
  getInputMaxLength: function (fieldData) {
    if (fieldData?.field_storage?.settings?.hasOwnProperty("max_length")) {
      return fieldData.field_storage.settings.max_length;
    }
    return null;
  },
  getAllowedFileMimeTypes: function (fieldData) {
    const mimeTypes = [];
    Object.entries(fieldData.settings.file_mime_types).forEach(([value]) => {
      mimeTypes.push(value);
    });
    return mimeTypes;
  },
  getPlaceholder: function (fieldData) {
    if (
      typeof fieldData === "object" &&
      typeof fieldData.form_displays.default.settings.placeholder !==
        "undefined"
    ) {
      return fieldData.form_displays.default.settings.placeholder;
    }
    return "";
  },
  getJSONapi: function (path, queries, callback, errorCallback, context) {
    jsonapiClient.getJSONapi(path, queries, callback, errorCallback, context);
  },

  gotoDestination: function () {
    // @todo roche_work: Consider also adding a query string to trigger a success
    // message at the destination.
    const urlParams = new URLSearchParams(window.location.search);
    let destination = urlParams.get("destination");
    if (destination) {
      // Strip leading slashes from internal paths to prevent them becoming
      // external URLs without protocol. /example.com should not be turned
      // into //example.com.
      const [, match] = destination.match(/^\/*(.*)/);
      // Add back one leading slash.
      destination = "/" + match;
    } else {
      destination = "/";
    }
    // Double check against external URLs.
    if (PathMapper.isLocal(destination)) {
      window.location.href = destination;
    }
    window.scrollTo(0, 0);
  },
  getConfigTermValuesByLabels: function (fieldConfig, labels) {
    const values = [];
    labels.forEach((label) => {
      Object.entries(
        fieldConfig?.settings?.allowed_values?.config_terms_term || {},
      ).forEach(([termUuid, termLabel]) => {
        if (termLabel === label) {
          values.push(termUuid);
        }
      });
    });
    return values;
  },
};

export default entityFormHelper;
